// Public Class Listings Page - Shows available classes for enrollment
import { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, Users, MapPin, BookOpen } from 'lucide-react';
import PortalLayout from './PortalLayout';
import { sanitizeQuillHtml } from '../../utils/sanitize';

const ClassListings = ({ teacherId, isDraft = false }) => {
  const [classes, setClasses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teacherInfo, setTeacherInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [extractedPortalSettings, setExtractedPortalSettings] = useState({});
  const [rightColumnHeight, setRightColumnHeight] = useState(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);

  const rightColumnRef = useRef(null);
  const classScheduleContentRef = useRef(null);

  useEffect(() => {
    fetchPublicData();
  }, [teacherId, isDraft]);

  // Check if we're on a large screen (lg breakpoint is 1024px in Tailwind)
  useEffect(() => {
    const checkScreenSize = () => {
      const isLg = window.innerWidth >= 1024;
      console.log('Screen width:', window.innerWidth, 'isLargeScreen:', isLg);
      setIsLargeScreen(isLg);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Measure right column height and apply to class schedule
  useEffect(() => {
    const measureHeight = () => {
      // Only measure on large screens
      if (!isLargeScreen) {
        console.log('Not measuring - not on large screen');
        setRightColumnHeight(null);
        return;
      }

      if (rightColumnRef.current) {
        const height = rightColumnRef.current.offsetHeight;
        console.log('Measured right column height:', height);
        setRightColumnHeight(height);
      } else {
        console.log('Right column ref not available');
      }
    };

    // Initial measurement after data loads
    if (!loading && isLargeScreen) {
      // Use longer timeout to ensure images and content are fully loaded
      setTimeout(measureHeight, 100);
      // Try again after a longer delay to catch images loading
      setTimeout(measureHeight, 500);
    }

    // Re-measure on window resize
    window.addEventListener('resize', measureHeight);
    return () => window.removeEventListener('resize', measureHeight);
  }, [loading, isLargeScreen, extractedPortalSettings.newsStories, extractedPortalSettings.upcomingProjects]);

  const fetchPublicData = async () => {
    try {
      setLoading(true);

      // Helper to fetch with auth token (required for draft)
      const fetchWithAuth = async (url) => {
        const token = localStorage.getItem('classtraks_token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const response = await fetch(url, { headers });
        return response.json();
      };

      // Determine which endpoint to use based on isDraft flag
      const teacherEndpoint = isDraft
        ? `/api/portal/${teacherId}/teacher/draft`
        : `/api/portal/${teacherId}/teacher`;

      // Fetch public classes and projects for this teacher
      const classesResponse = await fetchWithAuth(`/api/portal/${teacherId}/classes`);
      const teacherResponse = await fetchWithAuth(teacherEndpoint);
      const projectsResponse = await fetchWithAuth(`/api/portal/${teacherId}/projects`);

      setClasses(classesResponse.classes || []);
      setProjects(projectsResponse.projects || []);
      const teacher = teacherResponse.teacher || null;
      setTeacherInfo(teacher);

      // Extract portal settings from teacher info
      if (teacher) {
        console.log('ClassListings - teacher data:', teacher);
        console.log('ClassListings - teacher.logoIndent:', teacher.logoIndent);
        const portalSettings = {
          logo: teacher.logo,
          logoSize: teacher.logoSize || 'medium',
          logoIndent: teacher.logoIndent || 0,
          colorScheme: teacher.colorScheme || 'craft',
          businessName: teacher.businessName,
          tagline: teacher.tagline,
          contactEmail: teacher.contactEmail,
          contactPhone: teacher.contactPhone,
          address: teacher.address,
          welcomeMessage: teacher.welcomeMessage,
          heroImage: teacher.heroImage,
          heroTextAlign: teacher.heroTextAlign || 'center',
          heroTextBgEnabled: teacher.heroTextBgEnabled || false,
          heroTextBgColor: teacher.heroTextBgColor || '#000000',
          heroTextPlacement: teacher.heroTextPlacement || 'middle',
          backgroundImage: teacher.backgroundImage,
          backgroundPosition: teacher.backgroundPosition || 'fit',
          classScheduleMonths: teacher.classScheduleMonths || 3,
          upcomingProjects: teacher.upcomingProjects || [],
          newsStories: teacher.newsStories || [],
          // Header settings
          headerBackgroundColor: teacher.headerBackgroundColor || '#ffffff',
          headerHeight: teacher.headerHeight || 'medium',
          headerLayout: teacher.headerLayout || 'standard',
          // Social media settings
          showSocialMedia: teacher.showSocialMedia !== false,
          socialMedia: teacher.socialMedia || {},
          // Font settings
          headingFont: teacher.headingFont || 'system',
          bodyFont: teacher.bodyFont || 'system'
        };
        setExtractedPortalSettings(portalSettings);
      }
    } catch (error) {
      console.error('Error fetching public data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createLocalDate = (dateString) => {
    if (!dateString) return null;
    const utcDate = new Date(dateString);
    const year = utcDate.getUTCFullYear();
    const month = utcDate.getUTCMonth();
    const day = utcDate.getUTCDate();
    return new Date(year, month, day);
  };

  const filteredClasses = classes.filter(classItem => {
    // Filter out classes that have already passed (before current month)
    const now = new Date();
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    if (classItem.startDate) {
      const classDate = createLocalDate(classItem.startDate);
      const classMonth = new Date(classDate.getFullYear(), classDate.getMonth(), 1);

      if (classMonth < currentMonth) {
        return false;
      }
    }

    return true;
  });

  // Group classes by month and sort
  const groupClassesByMonth = (classes) => {
    const sortedClasses = classes.sort((a, b) => {
      if (!a.startDate && !b.startDate) return 0;
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      const dateA = createLocalDate(a.startDate);
      const dateB = createLocalDate(b.startDate);
      return dateA - dateB;
    });

    const grouped = {};
    sortedClasses.forEach(classItem => {
      if (!classItem.startDate) {
        const key = 'TBD';
        if (!grouped[key]) {
          grouped[key] = { monthYear: 'To Be Determined', classes: [] };
        }
        grouped[key].classes.push(classItem);
      } else {
        const date = createLocalDate(classItem.startDate);
        const monthYear = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        const sortKey = date.toISOString().slice(0, 7);

        if (!grouped[sortKey]) {
          grouped[sortKey] = { monthYear, classes: [] };
        }
        grouped[sortKey].classes.push(classItem);
      }
    });

    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'TBD') return 1;
        if (b === 'TBD') return -1;
        return a.localeCompare(b);
      })
      .map(key => grouped[key]);
  };

  const groupedClasses = groupClassesByMonth(filteredClasses);

  // Get featured/upcoming classes (next 3-4 classes)
  const upcomingClasses = filteredClasses.slice(0, 4);

  // Get footer color based on color scheme
  const getFooterColor = () => {
    const colorScheme = extractedPortalSettings.colorScheme || 'craft';
    const colorMap = {
      craft: '#4A5759',
      summer: '#5E6472',
      honestly: '#355070',
      sweets: '#F4978E',
      fabled: '#16425B'
    };
    return colorMap[colorScheme] || colorMap.craft;
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatSchedule = (classItem) => {
    const parts = [];

    if (classItem.startDate) {
      const startDate = createLocalDate(classItem.startDate);
      if (classItem.endDate) {
        const endDate = createLocalDate(classItem.endDate);
        if (startDate.toDateString() === endDate.toDateString()) {
          parts.push(startDate.toLocaleDateString());
        } else {
          parts.push(`${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`);
        }
      } else {
        parts.push(startDate.toLocaleDateString());
      }
    }

    if (classItem.startTime) {
      const [hours, minutes] = classItem.startTime.split(':');
      const time = new Date();
      time.setHours(parseInt(hours), parseInt(minutes));
      parts.push(`at ${time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`);
    }

    if (classItem.frequency && classItem.frequency !== 'One-time') {
      if (classItem.frequency === 'Weekly' && classItem.startDate) {
        const startDate = createLocalDate(classItem.startDate);
        const dayName = startDate.toLocaleDateString('en-US', { weekday: 'long' }) + 's';
        parts.push(`(${dayName})`);
      } else {
        parts.push(`(${classItem.frequency})`);
      }
    }

    return parts.length > 0 ? parts.join(' ') : 'Schedule TBD';
  };

  const getDayOfWeek = (classItem) => {
    if (classItem.frequency === 'Weekly' && classItem.startDate) {
      const startDate = createLocalDate(classItem.startDate);
      return startDate.toLocaleDateString('en-US', { weekday: 'long' }) + 's';
    }
    return null;
  };

  if (loading) {
    return (
      <PortalLayout teacherInfo={teacherInfo} portalSettings={extractedPortalSettings} userId={teacherId}>
        <div className="flex justify-center items-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout teacherInfo={teacherInfo} portalSettings={extractedPortalSettings} userId={teacherId}>
      {/* Hero Section - Full width image with overlay text */}
      <div className="relative mb-6" style={{ marginTop: '-2rem' }}>
        <div className="relative h-80 overflow-hidden rounded-b-lg">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: extractedPortalSettings.heroImage
                ? `url(${extractedPortalSettings.heroImage})`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              filter: 'brightness(0.7)'
            }}
          />
          <div
            className={`relative z-10 h-full flex items-end justify-end lg:items-center ${
              extractedPortalSettings.heroTextPlacement === 'left' ? 'lg:justify-start' :
              extractedPortalSettings.heroTextPlacement === 'right' ? 'lg:justify-end' :
              'lg:justify-center'
            }`}
            style={{ padding: '20px' }}
          >
            <div
              className="inline-block"
              style={{
                backgroundColor: extractedPortalSettings.heroTextBgEnabled
                  ? `${extractedPortalSettings.heroTextBgColor || '#000000'}B3`
                  : 'transparent',
                borderRadius: extractedPortalSettings.heroTextBgEnabled ? '0.5rem' : '0',
                padding: '20px',
                wordWrap: 'break-word',
                maxWidth: '90%'
              }}
            >
              <div
                className="text-2xl sm:text-3xl font-bold text-white drop-shadow-lg"
                style={{
                  fontFamily: "'DM Serif Display', serif"
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeQuillHtml(
                    extractedPortalSettings.welcomeMessage ||
                    "Lessons designed to bring out your creativity."
                  )
                }}
              />
            </div>
          </div>
        </div>
      </div>


      {/* Two Column Layout */}
      <div className="flex flex-col lg:flex-row lg:items-start" style={{ gap: '26px', fontFamily: "'Inter', sans-serif" }}>
        {/* Left Column - Class Schedule (30% width) */}
        <div className="lg:w-[30%]">
          <div
            className="bg-white rounded-lg shadow-sm overflow-hidden flex flex-col"
            style={{
              height: rightColumnHeight ? `${rightColumnHeight}px` : 'auto'
            }}
            ref={(el) => {
              if (el) console.log('Class Schedule container height set to:', el.style.height, 'actual height:', el.offsetHeight);
            }}
          >
            {/* Header */}
            <div className="px-6 py-3" style={{ backgroundColor: '#de766f' }}>
              <h2 className="text-lg font-bold text-white">Class Schedule</h2>
            </div>

            {/* Class Listings - Dynamic Months */}
            <div
              ref={classScheduleContentRef}
              className="p-4 flex-1 overflow-y-auto"
            >
              {(() => {
                // Filter classes based on configured months setting
                const now = new Date();
                const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
                const monthsToShow = extractedPortalSettings.classScheduleMonths || 3;
                const futureMonthLimit = new Date(now.getFullYear(), now.getMonth() + monthsToShow, 1);

                const recentClasses = filteredClasses.filter(classItem => {
                  if (!classItem.startDate) return false;
                  const classDate = createLocalDate(classItem.startDate);
                  const classMonth = new Date(classDate.getFullYear(), classDate.getMonth(), 1);
                  return classMonth >= currentMonth && classMonth < futureMonthLimit;
                });

                if (recentClasses.length === 0) {
                  return (
                    <div className="text-center py-8">
                      <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600">No upcoming classes</p>
                    </div>
                  );
                }

                // Group classes by month
                const groupedByMonth = {};
                recentClasses.forEach(classItem => {
                  const classDate = createLocalDate(classItem.startDate);
                  const monthYear = classDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  const sortKey = classDate.toISOString().slice(0, 7); // YYYY-MM format for sorting

                  if (!groupedByMonth[sortKey]) {
                    groupedByMonth[sortKey] = {
                      monthYear,
                      classes: []
                    };
                  }
                  groupedByMonth[sortKey].classes.push(classItem);
                });

                // Sort by month
                const sortedMonthGroups = Object.keys(groupedByMonth)
                  .sort()
                  .map(key => groupedByMonth[key]);

                return (
                  <div className="space-y-8">
                    {sortedMonthGroups.map((monthGroup, monthIndex) => (
                      <div key={monthIndex} className="mb-6">
                        {/* Month Header */}
                        <h3 className="text-sm font-bold text-gray-700 mb-3 px-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" style={{ color: '#de766f' }} />
                          {monthGroup.monthYear}
                        </h3>

                        {/* Classes for this month */}
                        <div className="space-y-4">
                          {monthGroup.classes.map((classItem) => {
                            const spotsLeft = (classItem.maxStudents || classItem.maxCapacity || 0) - (classItem.enrolledCount || classItem.currentEnrollment || 0);
                            const isFull = spotsLeft <= 0;
                            const hasWaitlist = classItem.waitlistEnabled;
                            const project = classItem.project;

                            return (
                              <div
                                key={classItem.id}
                                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                onClick={() => {
                                  if (isFull && hasWaitlist) {
                                    window.location.href = `/waitlist/${classItem.id}`;
                                  } else if (!isFull) {
                                    window.location.href = `/portal/${teacherId}/enroll/${classItem.id}`;
                                  }
                                }}
                              >
                                {/* Class Info */}
                                <div className="p-3">
                                  <h4 className="font-bold text-sm text-gray-900 mb-2 line-clamp-2">
                                    {classItem.title}
                                  </h4>

                                  <div className="text-xs text-gray-600 mb-2 flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {formatSchedule(classItem)}
                                  </div>

                                  <div className="flex items-center justify-between">
                                    {!isFull ? (
                                      <span
                                        className="px-3 py-1 rounded-full text-sm font-bold text-white"
                                        style={{ backgroundColor: '#6fd7de' }}
                                      >
                                        {formatPrice(classItem.price)}
                                      </span>
                                    ) : hasWaitlist ? (
                                      <span
                                        className="px-3 py-1 rounded-full text-sm font-bold bg-amber-500 text-white"
                                      >
                                        Join Waitlist
                                      </span>
                                    ) : (
                                      <span
                                        className="px-3 py-1 rounded-full text-sm font-bold bg-gray-400 text-white"
                                      >
                                        Full
                                      </span>
                                    )}
                                    <span className="text-xs text-gray-600">
                                      {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Right Column - News & Upcoming Projects (70% width) */}
        <div className="lg:w-[70%]" ref={rightColumnRef}>
          <div className="space-y-6">
            {/* News Section */}
            {extractedPortalSettings.newsStories && extractedPortalSettings.newsStories.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {/* Header */}
                <div className="px-6 py-3" style={{ backgroundColor: '#de766f' }}>
                  <h2 className="text-lg font-bold text-white">Latest News</h2>
                </div>

                {/* News Stories */}
                <div className="p-4 space-y-6">
                  {extractedPortalSettings.newsStories.map((story, index) => (
                    <div key={story.id || index} className="border-b border-gray-200 pb-6 last:border-b-0">
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* News Photo */}
                        {story.photoUrl && (
                          <div className="md:w-1/3 flex-shrink-0">
                            <img
                              src={story.photoUrl}
                              alt={story.header}
                              className="w-full h-48 md:h-full object-cover rounded-lg"
                            />
                          </div>
                        )}

                        {/* News Content */}
                        <div className={story.photoUrl ? 'md:w-2/3' : 'w-full'}>
                          <h3 className="font-bold text-gray-900 mb-3">
                            {story.header}
                          </h3>
                          <div
                            className="text-sm text-gray-600"
                            dangerouslySetInnerHTML={{ __html: sanitizeQuillHtml(story.body) }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upcoming Projects Section */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden sticky top-4">
              {/* Header */}
              <div className="px-6 py-3" style={{ backgroundColor: '#de766f' }}>
                <h2 className="text-lg font-bold text-white">Upcoming Projects</h2>
              </div>

              {/* Featured Projects */}
              <div className="p-4 space-y-6">
              {(!extractedPortalSettings.upcomingProjects || extractedPortalSettings.upcomingProjects.length === 0) ? (
                <p className="text-gray-500 text-center py-8">No upcoming projects configured</p>
              ) : (
                extractedPortalSettings.upcomingProjects.map((configuredProject, index) => {
                  // Find the actual project data
                  const project = projects.find(p => p.id === configuredProject.projectId);

                  if (!project) return null;

                  // Find all classes associated with this project
                  const projectClasses = classes.filter(c => c.projectId === configuredProject.projectId);

                  return (
                    <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                      {/* Month Header */}
                      {configuredProject.monthHeader && (
                        <div className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-2">
                          <Calendar className="w-4 h-4" style={{ color: '#de766f' }} />
                          {configuredProject.monthHeader}
                        </div>
                      )}

                      {/* Project Title */}
                      <h3 className="font-bold text-gray-900 mb-3">
                        {project.name}
                      </h3>

                      {/* Horizontal Layout: Image Left, Content Right */}
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Featured Image - 4:3 Aspect Ratio */}
                        {project.imageUrl && (
                          <div className="flex-shrink-0 w-full sm:w-48">
                            <div className="relative rounded-lg overflow-hidden" style={{ paddingBottom: '75%' }}>
                              <img
                                src={project.imageUrl}
                                alt={project.name}
                                className="absolute top-0 left-0 w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}

                        {/* Content Column */}
                        <div className="flex-1 flex flex-col">
                          {/* Custom Description or Project Default */}
                          <p className="text-sm text-gray-600 mb-4">
                            {configuredProject.description || project.description || ''}
                          </p>

                          {/* Class Buttons */}
                          <div className="flex flex-wrap gap-2 mt-auto">
                            {projectClasses.length > 0 ? (
                              projectClasses.map((classItem) => {
                                const dayOfWeek = getDayOfWeek(classItem);
                                const spotsLeft = (classItem.maxStudents || classItem.maxCapacity || 0) - (classItem.enrolledCount || classItem.currentEnrollment || 0);
                                const isFull = spotsLeft <= 0;
                                const hasWaitlist = classItem.waitlistEnabled;

                                let buttonLabel = dayOfWeek
                                  ? `${classItem.title} - ${dayOfWeek}`
                                  : classItem.title;

                                if (isFull && hasWaitlist) {
                                  buttonLabel += ' - Join Waitlist';
                                } else if (isFull) {
                                  buttonLabel += ' - Full';
                                }

                                return (
                                  <button
                                    key={classItem.id}
                                    onClick={() => {
                                      if (isFull && hasWaitlist) {
                                        window.location.href = `/waitlist/${classItem.id}`;
                                      } else if (!isFull) {
                                        window.location.href = `/portal/${teacherId}/enroll/${classItem.id}`;
                                      }
                                    }}
                                    className="px-4 py-2 rounded-full text-xs font-semibold text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ backgroundColor: isFull && !hasWaitlist ? '#9ca3af' : (isFull && hasWaitlist ? '#f59e0b' : '#de766f') }}
                                    disabled={isFull && !hasWaitlist}
                                  >
                                    {buttonLabel}
                                  </button>
                                );
                              })
                            ) : (
                              <span className="px-4 py-2 rounded-full text-xs font-semibold bg-gray-300 text-gray-600 cursor-not-allowed">
                                Coming Soon
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      {/* Social Media / Instagram Section */}
      <div className="mt-16 bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Follow Us on Social Media
        </h2>
        <p className="text-gray-600 mb-6">
          Interested in joining the community? Like our page and share {extractedPortalSettings.businessName || 'our'} content and courses!
        </p>
        {teacherInfo?.socialMedia?.instagram && (
          <a
            href={teacherInfo.socialMedia.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all"
          >
            📸 Follow on Instagram
          </a>
        )}
        {teacherInfo?.socialMedia?.facebook && (
          <a
            href={teacherInfo.socialMedia.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-all ml-4"
          >
            Facebook
          </a>
        )}
      </div>
    </PortalLayout>
  );
};

export default ClassListings;
